import { SIDEBAR_QUERY_PARAM } from '@app/Sidebar/constants';
import { TableId } from '@components/Table';
import { TablePreferencesSettings } from '@components/Table/util/customizeTable';
import { submitMutation } from '@components/shared/mutation';
import { useFlagMe470466EnableStoringReferenceVisibilityInTablePreferences } from '@generated/flags/ME-470466-enable-storing-reference-visibility-in-table-preferences';
import { useUpdateUserReferenceTypesMutation } from '@generated/mutations/updateUserReferenceTypes';
import { useGetUserReferenceTypesLazyQuery } from '@generated/queries/getUserReferenceTypes';
import {
  KEY_REFERENCE_VISIBILITY_PREFERENCES_MIGRATED,
  KEY_USER_PREFERENCES,
} from '@utils/constants';
import { noTopLevelErrors } from '@utils/graphqlUtils';
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from '@utils/localStorage';
import { reportCustomError } from '@utils/reportCustomError';
import { isEqual, noop, pickBy } from 'lodash-es';
import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface RoutesReferenceVisibilityPreference {
  id: string;
  label: string;
  referenceType: string;
}

const REFERENCE_VISIBILITY_PREFERENCE_FIELDS = [
  'availableRoutesReferenceVisibility',
  'mprbReferenceVisibility',
  'trackingReferenceVisibility',
  'driverTimelineReferenceVisibility',
  'drayageReferenceVisibility',
] as const;

export type ReferenceVisibilityPreferenceField =
  (typeof REFERENCE_VISIBILITY_PREFERENCE_FIELDS)[number];

export interface UserPreferencesState {
  mostRecentCarrier?: string;
  sidebarOpen?: boolean;
  tablePreferences?: Record<TableId, TablePreferencesSettings[]>;
  selectedRoutePreference?: string;
  fullstoryDebug?: boolean;
  localizationDebug?: boolean;
  preferMilitaryTime?: boolean;
  newWindowOptOut?: boolean;
  availableRoutesReferenceVisibility?: RoutesReferenceVisibilityPreference[];
  mprbReferenceVisibility?: RoutesReferenceVisibilityPreference[];
  trackingReferenceVisibility?: RoutesReferenceVisibilityPreference[];
  driverTimelineReferenceVisibility?: RoutesReferenceVisibilityPreference[];
  drayageReferenceVisibility?: RoutesReferenceVisibilityPreference[];
  referenceVisibilityUpdatedAtByField?: ReferenceVisibilityUpdatedAtByField;
  /** True once reference-type preferences have been migrated to API-backed storage. */
  referenceTypeStorageMigrated?: boolean;
  /** Used internally for PR previews to point to dev or test */
  prDomain?: string;
}

type SetPrefsArg = Partial<UserPreferencesState>;

type ReferenceVisibilityUpdatedAtByField = Partial<
  Record<ReferenceVisibilityPreferenceField, string>
>;

interface ServerReferencePreferenceValue {
  referenceTypes: RoutesReferenceVisibilityPreference[];
  updatedAt?: string;
}

type ServerReferencePreferencesByField = Partial<
  Record<ReferenceVisibilityPreferenceField, ServerReferencePreferenceValue>
>;

type NormalizedReferencePreferences = Record<
  ReferenceVisibilityPreferenceField,
  RoutesReferenceVisibilityPreference[]
>;

interface ReferenceVisibilityMigrationResult {
  didMigrate: boolean;
  didFetchServerPreferences: boolean;
  resolvedPreferences: NormalizedReferencePreferences;
  resolvedUpdatedAtByField: ReferenceVisibilityUpdatedAtByField;
}

interface UserPreferencesActions {
  setPrefs: (userPreferences: SetPrefsArg) => void;
  removePrefs: () => void;
  migrateReferenceVisibilityPreferences: () => Promise<boolean>;
  getReferenceVisibilityPreferences: (
    field: ReferenceVisibilityPreferenceField,
  ) => Promise<RoutesReferenceVisibilityPreference[]>;
  saveReferenceVisibilityPreferences: (
    field: ReferenceVisibilityPreferenceField,
    referenceTypes: RoutesReferenceVisibilityPreference[],
  ) => Promise<boolean>;
}

const rawDefaults: UserPreferencesState = {
  mostRecentCarrier: '',
  tablePreferences: {} as Record<TableId, TablePreferencesSettings[]>,
  sidebarOpen: true,
  selectedRoutePreference: '',
  fullstoryDebug: false,
  preferMilitaryTime: false,
  availableRoutesReferenceVisibility: [],
  mprbReferenceVisibility: [],
  trackingReferenceVisibility: [],
  driverTimelineReferenceVisibility: [],
  drayageReferenceVisibility: [],
  referenceVisibilityUpdatedAtByField: {},
  referenceTypeStorageMigrated: false,
};

const sanitizeReferenceVisibilityUpdatedAtByField = (
  value: UserPreferencesState['referenceVisibilityUpdatedAtByField'],
): ReferenceVisibilityUpdatedAtByField => {
  const nextValue: ReferenceVisibilityUpdatedAtByField = {};

  REFERENCE_VISIBILITY_PREFERENCE_FIELDS.forEach((field) => {
    const timestamp = value?.[field];
    if (typeof timestamp === 'string' && timestamp.length > 0) {
      nextValue[field] = timestamp;
    }
  });

  return nextValue;
};

const getReferenceVisibilityUpdatedAtByFieldFromState = (
  state: UserPreferencesState,
): ReferenceVisibilityUpdatedAtByField => {
  return sanitizeReferenceVisibilityUpdatedAtByField(
    state.referenceVisibilityUpdatedAtByField,
  );
};

const mergeReferenceVisibilityUpdatedAtByField = (
  ...values: ReadonlyArray<
    UserPreferencesState['referenceVisibilityUpdatedAtByField']
  >
): ReferenceVisibilityUpdatedAtByField => {
  return values.reduce<ReferenceVisibilityUpdatedAtByField>((acc, value) => {
    const sanitizedValue = sanitizeReferenceVisibilityUpdatedAtByField(value);

    return {
      ...acc,
      ...sanitizedValue,
    };
  }, {});
};

const getCurrentIsoTimestamp = (): string => {
  return new Date().toISOString();
};

const backfillMissingReferenceVisibilityUpdatedAtByField = (
  state: Partial<UserPreferencesState>,
  value: ReferenceVisibilityUpdatedAtByField,
): ReferenceVisibilityUpdatedAtByField => {
  const nextValue: ReferenceVisibilityUpdatedAtByField = { ...value };

  REFERENCE_VISIBILITY_PREFERENCE_FIELDS.forEach((field) => {
    if (nextValue[field]) {
      return;
    }

    const referenceTypes = state[field];
    if (!Array.isArray(referenceTypes) || referenceTypes.length === 0) {
      return;
    }

    nextValue[field] = getCurrentIsoTimestamp();
  });

  return nextValue;
};

const toTimestampMillis = (value?: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) {
    return undefined;
  }

  return timestamp;
};

const isServerUpdatedAtNewer = (
  localUpdatedAt?: string,
  serverUpdatedAt?: string,
): boolean => {
  const localTimestamp = toTimestampMillis(localUpdatedAt);
  const serverTimestamp = toTimestampMillis(serverUpdatedAt);

  if (localTimestamp === undefined || serverTimestamp === undefined) {
    return false;
  }

  return serverTimestamp > localTimestamp;
};

const getMostRecentUpdatedAt = (
  localUpdatedAt?: string,
  serverUpdatedAt?: string,
): string | undefined => {
  const localTimestamp = toTimestampMillis(localUpdatedAt);
  const serverTimestamp = toTimestampMillis(serverUpdatedAt);

  if (localTimestamp !== undefined && serverTimestamp !== undefined) {
    return serverTimestamp > localTimestamp ? serverUpdatedAt : localUpdatedAt;
  }

  if (localUpdatedAt) {
    return localUpdatedAt;
  }

  return serverUpdatedAt;
};

const hasLocalReferenceSignal = (
  referenceTypes: RoutesReferenceVisibilityPreference[],
  updatedAt?: string,
): boolean => {
  return referenceTypes.length > 0 || Boolean(updatedAt);
};

export const getInitialPrefsFromStorage = (): UserPreferencesState => {
  try {
    const fromStorage =
      getLocalStorage<UserPreferencesState>(KEY_USER_PREFERENCES) ?? {};
    const referenceVisibilityUpdatedAtByField =
      sanitizeReferenceVisibilityUpdatedAtByField(
        fromStorage.referenceVisibilityUpdatedAtByField,
      );
    const referenceVisibilityUpdatedAtByFieldWithBackfill =
      backfillMissingReferenceVisibilityUpdatedAtByField(
        fromStorage,
        referenceVisibilityUpdatedAtByField,
      );
    const migratedMarkerFromStorage =
      getLocalStorage<boolean>(
        KEY_REFERENCE_VISIBILITY_PREFERENCES_MIGRATED,
      ) === true;
    const referenceTypeStorageMigrated =
      fromStorage.referenceTypeStorageMigrated === true ||
      migratedMarkerFromStorage;
    const amendedFromStorage: UserPreferencesState = pickBy(
      {
        ...fromStorage,
        sidebarOpen: SIDEBAR_QUERY_PARAM ?? fromStorage.sidebarOpen ?? null,
        referenceVisibilityUpdatedAtByField:
          referenceVisibilityUpdatedAtByFieldWithBackfill,
        referenceTypeStorageMigrated,
      },
      (v) => v !== null,
    );
    if (referenceTypeStorageMigrated) {
      setLocalStorage(KEY_REFERENCE_VISIBILITY_PREFERENCES_MIGRATED, true);
    }
    if (!isEqual(fromStorage, amendedFromStorage)) {
      setLocalStorage(KEY_USER_PREFERENCES, amendedFromStorage);
    }
    return {
      ...rawDefaults,
      ...amendedFromStorage,
    };
  } catch {
    return { ...rawDefaults };
  }
};

const mapServerReferencePreferences = (
  values: ReadonlyArray<{
    modalId: string;
    referenceTypes: ReadonlyArray<{
      id: string;
      label: string;
      referenceType: string;
    }>;
    updatedAt?: string | null;
  }>,
): ServerReferencePreferencesByField => {
  return values.reduce<ServerReferencePreferencesByField>((acc, item) => {
    const field = item.modalId as ReferenceVisibilityPreferenceField;
    if (!REFERENCE_VISIBILITY_PREFERENCE_FIELDS.includes(field)) {
      return acc;
    }

    acc[field] = {
      referenceTypes: item.referenceTypes.map((referenceType) => ({
        id: referenceType.id,
        label: referenceType.label,
        referenceType: referenceType.referenceType,
      })),
      updatedAt: item.updatedAt ?? undefined,
    };

    return acc;
  }, {});
};

const sanitizeReferenceType = (
  value: RoutesReferenceVisibilityPreference,
): RoutesReferenceVisibilityPreference => {
  return {
    id: value.id,
    label: value.label,
    referenceType: value.referenceType,
  };
};

const sanitizeReferenceTypes = (
  values: ReadonlyArray<RoutesReferenceVisibilityPreference>,
): RoutesReferenceVisibilityPreference[] => {
  return values.map((value) => sanitizeReferenceType(value));
};

const hasServerReferenceField = (
  value: ServerReferencePreferencesByField,
  field: ReferenceVisibilityPreferenceField,
): boolean => {
  return Object.prototype.hasOwnProperty.call(value, field);
};

const getReferencePreferencesFromState = (
  state: UserPreferencesState,
): NormalizedReferencePreferences => {
  return {
    availableRoutesReferenceVisibility: sanitizeReferenceTypes(
      state.availableRoutesReferenceVisibility ?? [],
    ),
    mprbReferenceVisibility: sanitizeReferenceTypes(
      state.mprbReferenceVisibility ?? [],
    ),
    trackingReferenceVisibility: sanitizeReferenceTypes(
      state.trackingReferenceVisibility ?? [],
    ),
    driverTimelineReferenceVisibility: sanitizeReferenceTypes(
      state.driverTimelineReferenceVisibility ?? [],
    ),
    drayageReferenceVisibility: sanitizeReferenceTypes(
      state.drayageReferenceVisibility ?? [],
    ),
  };
};

function useUserPrefs(): [UserPreferencesState, UserPreferencesActions] {
  const [statePrefs, setStoredValue] = useState<UserPreferencesState>(
    (): UserPreferencesState => getInitialPrefsFromStorage(),
  );
  const statePrefsRef = useRef<UserPreferencesState>(statePrefs);
  const migrationInFlightRef =
    useRef<Promise<ReferenceVisibilityMigrationResult> | null>(null);

  useEffect(() => {
    statePrefsRef.current = statePrefs;
  }, [statePrefs]);

  const [fetchUserReferenceTypes] = useGetUserReferenceTypesLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [updateUserReferenceTypes] = useUpdateUserReferenceTypesMutation();
  const useReferenceVisibilityApi =
    useFlagMe470466EnableStoringReferenceVisibilityInTablePreferences();

  // console.log('useReferenceVisibilityApi: ', useReferenceVisibilityApi);

  const setPrefs = useCallback(
    (rawValues: SetPrefsArg): void => {
      try {
        const prevFromStorage =
          getLocalStorage<UserPreferencesState>(KEY_USER_PREFERENCES);
        const migratedMarkerFromStorage =
          getLocalStorage<boolean>(
            KEY_REFERENCE_VISIBILITY_PREFERENCES_MIGRATED,
          ) === true;
        const mergedReferenceVisibilityUpdatedAtByField =
          mergeReferenceVisibilityUpdatedAtByField(
            prevFromStorage?.referenceVisibilityUpdatedAtByField,
            statePrefsRef.current.referenceVisibilityUpdatedAtByField,
            rawValues.referenceVisibilityUpdatedAtByField,
          );

        REFERENCE_VISIBILITY_PREFERENCE_FIELDS.forEach((field) => {
          if (rawValues[field] === undefined) {
            return;
          }

          if (mergedReferenceVisibilityUpdatedAtByField[field]) {
            return;
          }

          mergedReferenceVisibilityUpdatedAtByField[field] =
            getCurrentIsoTimestamp();
        });
        const referenceVisibilityUpdatedAtByFieldWithBackfill =
          backfillMissingReferenceVisibilityUpdatedAtByField(
            {
              ...prevFromStorage,
              ...rawValues,
            },
            mergedReferenceVisibilityUpdatedAtByField,
          );

        const referenceTypeStorageMigrated =
          rawValues.referenceTypeStorageMigrated ??
          prevFromStorage?.referenceTypeStorageMigrated ??
          statePrefsRef.current.referenceTypeStorageMigrated ??
          migratedMarkerFromStorage;
        const finalValues = {
          ...prevFromStorage,
          // Exclude sidebar from cross-tab sync - it should remain more stable per tab - but still allow it to be set for tab reload or new tab.
          sidebarOpen: statePrefs.sidebarOpen,
          ...rawValues,
          referenceVisibilityUpdatedAtByField:
            referenceVisibilityUpdatedAtByFieldWithBackfill,
          referenceTypeStorageMigrated,
        };
        setStoredValue(finalValues);
        setLocalStorage(KEY_USER_PREFERENCES, finalValues);
        if (finalValues.referenceTypeStorageMigrated) {
          setLocalStorage(KEY_REFERENCE_VISIBILITY_PREFERENCES_MIGRATED, true);
        } else {
          removeLocalStorage(KEY_REFERENCE_VISIBILITY_PREFERENCES_MIGRATED);
        }
      } catch {
        // If user is in private mode or has storage restriction
        // localStorage can throw. Also JSON.stringify can throw.
      }
    },
    [statePrefs.sidebarOpen],
  );

  const removePrefs = (): void => {
    setPrefs(rawDefaults);
  };

  const runReferenceVisibilityMigration =
    useCallback(async (): Promise<ReferenceVisibilityMigrationResult> => {
      const localFallback = getReferencePreferencesFromState(
        statePrefsRef.current,
      );
      const localUpdatedAtByField =
        getReferenceVisibilityUpdatedAtByFieldFromState(statePrefsRef.current);

      if (!useReferenceVisibilityApi) {
        return {
          didMigrate: false,
          didFetchServerPreferences: false,
          resolvedPreferences: localFallback,
          resolvedUpdatedAtByField: localUpdatedAtByField,
        };
      }

      const migratedMarkerFromStorage =
        getLocalStorage<boolean>(
          KEY_REFERENCE_VISIBILITY_PREFERENCES_MIGRATED,
        ) === true;

      if (migratedMarkerFromStorage) {
        if (!statePrefsRef.current.referenceTypeStorageMigrated) {
          setPrefs({ referenceTypeStorageMigrated: true });
        }
        return {
          didMigrate: true,
          didFetchServerPreferences: false,
          resolvedPreferences: localFallback,
          resolvedUpdatedAtByField: localUpdatedAtByField,
        };
      }

      if (statePrefsRef.current.referenceTypeStorageMigrated) {
        return {
          didMigrate: true,
          didFetchServerPreferences: false,
          resolvedPreferences: localFallback,
          resolvedUpdatedAtByField: localUpdatedAtByField,
        };
      }

      if (migrationInFlightRef.current) {
        return migrationInFlightRef.current;
      }

      const migrationPromise =
        (async (): Promise<ReferenceVisibilityMigrationResult> => {
          try {
            const response = await fetchUserReferenceTypes();
            const serverPreferencesRaw = mapServerReferencePreferences(
              response.data?.getUserReferenceTypes ?? [],
            );
            const resolvedPreferences: NormalizedReferencePreferences = {
              ...localFallback,
            };
            const resolvedUpdatedAtByField: ReferenceVisibilityUpdatedAtByField =
              {
                ...localUpdatedAtByField,
              };
            const fieldsNeedingMigration: ReferenceVisibilityPreferenceField[] =
              [];

            REFERENCE_VISIBILITY_PREFERENCE_FIELDS.forEach((field) => {
              const localReferenceTypes = localFallback[field];
              const localUpdatedAt = localUpdatedAtByField[field];
              const hasServerField = hasServerReferenceField(
                serverPreferencesRaw,
                field,
              );
              const serverReferenceTypes =
                serverPreferencesRaw[field]?.referenceTypes ?? [];
              const serverUpdatedAt = serverPreferencesRaw[field]?.updatedAt;
              const localHasSignal = hasLocalReferenceSignal(
                localReferenceTypes,
                localUpdatedAt,
              );

              if (!hasServerField && !localHasSignal) {
                return;
              }

              if (!hasServerField && localHasSignal) {
                fieldsNeedingMigration.push(field);
                return;
              }

              if (hasServerField && !localHasSignal) {
                resolvedPreferences[field] = serverReferenceTypes;
                if (serverUpdatedAt) {
                  resolvedUpdatedAtByField[field] = serverUpdatedAt;
                }
                return;
              }

              if (isServerUpdatedAtNewer(localUpdatedAt, serverUpdatedAt)) {
                resolvedPreferences[field] = serverReferenceTypes;
                if (serverUpdatedAt) {
                  resolvedUpdatedAtByField[field] = serverUpdatedAt;
                }
                return;
              }

              resolvedPreferences[field] = localReferenceTypes;

              if (isEqual(localReferenceTypes, serverReferenceTypes)) {
                const mostRecentUpdatedAt = getMostRecentUpdatedAt(
                  localUpdatedAt,
                  serverUpdatedAt,
                );
                if (mostRecentUpdatedAt) {
                  resolvedUpdatedAtByField[field] = mostRecentUpdatedAt;
                }
                return;
              }

              fieldsNeedingMigration.push(field);
            });

            for (const field of fieldsNeedingMigration) {
              const mutationResponse = await submitMutation(
                () =>
                  updateUserReferenceTypes({
                    variables: {
                      input: {
                        modalId: field,
                        referenceTypes: sanitizeReferenceTypes(
                          localFallback[field],
                        ),
                      },
                    },
                  }),
                {
                  hideSuccessToast: true,
                },
              );

              if (!noTopLevelErrors(mutationResponse)) {
                return {
                  didMigrate: false,
                  didFetchServerPreferences: true,
                  resolvedPreferences: localFallback,
                  resolvedUpdatedAtByField: localUpdatedAtByField,
                };
              }

              resolvedUpdatedAtByField[field] =
                mutationResponse.data?.updateUserReferenceTypes.updatedAt ??
                localUpdatedAtByField[field] ??
                getCurrentIsoTimestamp();
            }

            setPrefs({
              ...resolvedPreferences,
              referenceVisibilityUpdatedAtByField: resolvedUpdatedAtByField,
              referenceTypeStorageMigrated: true,
            });

            return {
              didMigrate: true,
              didFetchServerPreferences: true,
              resolvedPreferences,
              resolvedUpdatedAtByField,
            };
          } catch (error) {
            reportCustomError(error as Error);
            return {
              didMigrate: false,
              didFetchServerPreferences: true,
              resolvedPreferences: localFallback,
              resolvedUpdatedAtByField: localUpdatedAtByField,
            };
          }
        })();

      migrationInFlightRef.current = migrationPromise;

      try {
        return await migrationPromise;
      } finally {
        migrationInFlightRef.current = null;
      }
    }, [
      fetchUserReferenceTypes,
      setPrefs,
      updateUserReferenceTypes,
      useReferenceVisibilityApi,
    ]);

  const migrateReferenceVisibilityPreferences =
    useCallback(async (): Promise<boolean> => {
      const migrationResult = await runReferenceVisibilityMigration();
      return migrationResult.didMigrate;
    }, [runReferenceVisibilityMigration]);

  const getReferenceVisibilityPreferences = useCallback(
    async (
      field: ReferenceVisibilityPreferenceField,
    ): Promise<RoutesReferenceVisibilityPreference[]> => {
      const localFallback = getReferencePreferencesFromState(
        statePrefsRef.current,
      );

      if (!useReferenceVisibilityApi) {
        return localFallback[field] ?? [];
      }

      const migrationResult = await runReferenceVisibilityMigration();
      if (!migrationResult.didMigrate) {
        return localFallback[field] ?? [];
      }

      if (migrationResult.didFetchServerPreferences) {
        return migrationResult.resolvedPreferences[field] ?? [];
      }

      try {
        const response = await fetchUserReferenceTypes();
        const serverPreferencesRaw = mapServerReferencePreferences(
          response.data?.getUserReferenceTypes ?? [],
        );
        const refreshedFallback = getReferencePreferencesFromState(
          statePrefsRef.current,
        );
        const refreshedUpdatedAtByField =
          getReferenceVisibilityUpdatedAtByFieldFromState(
            statePrefsRef.current,
          );
        const resolvedPreferences: NormalizedReferencePreferences = {
          ...refreshedFallback,
        };
        const resolvedUpdatedAtByField: ReferenceVisibilityUpdatedAtByField = {
          ...refreshedUpdatedAtByField,
        };

        REFERENCE_VISIBILITY_PREFERENCE_FIELDS.forEach((preferenceField) => {
          const hasServerField = hasServerReferenceField(
            serverPreferencesRaw,
            preferenceField,
          );

          if (!hasServerField) {
            return;
          }

          const serverReferenceTypes =
            serverPreferencesRaw[preferenceField]?.referenceTypes ?? [];
          const serverUpdatedAt =
            serverPreferencesRaw[preferenceField]?.updatedAt;
          const localReferenceTypes = refreshedFallback[preferenceField];
          const localUpdatedAt = refreshedUpdatedAtByField[preferenceField];
          const localHasSignal = hasLocalReferenceSignal(
            localReferenceTypes,
            localUpdatedAt,
          );

          if (
            !localHasSignal ||
            isServerUpdatedAtNewer(localUpdatedAt, serverUpdatedAt)
          ) {
            resolvedPreferences[preferenceField] = serverReferenceTypes;
            if (serverUpdatedAt) {
              resolvedUpdatedAtByField[preferenceField] = serverUpdatedAt;
            }
            return;
          }

          const mostRecentUpdatedAt = getMostRecentUpdatedAt(
            localUpdatedAt,
            serverUpdatedAt,
          );
          if (mostRecentUpdatedAt) {
            resolvedUpdatedAtByField[preferenceField] = mostRecentUpdatedAt;
          }
        });

        setPrefs({
          ...resolvedPreferences,
          referenceVisibilityUpdatedAtByField: resolvedUpdatedAtByField,
        });

        return resolvedPreferences[field] ?? [];
      } catch (error) {
        reportCustomError(error as Error);
        const postMigrationPrefs = getReferencePreferencesFromState(
          statePrefsRef.current,
        );
        return postMigrationPrefs[field] ?? [];
      }
    },
    [
      fetchUserReferenceTypes,
      runReferenceVisibilityMigration,
      setPrefs,
      useReferenceVisibilityApi,
    ],
  );

  const saveReferenceVisibilityPreferences = useCallback(
    async (
      field: ReferenceVisibilityPreferenceField,
      referenceTypes: RoutesReferenceVisibilityPreference[],
    ): Promise<boolean> => {
      const sanitizedReferenceTypes = sanitizeReferenceTypes(referenceTypes);

      if (!useReferenceVisibilityApi) {
        const nextPreferenceState: SetPrefsArg = {};
        nextPreferenceState[field] = sanitizedReferenceTypes;
        nextPreferenceState.referenceVisibilityUpdatedAtByField = {
          [field]: getCurrentIsoTimestamp(),
        };
        nextPreferenceState.referenceTypeStorageMigrated = false;
        setPrefs(nextPreferenceState);
        return true;
      }

      const didMigrate = await migrateReferenceVisibilityPreferences();
      if (!didMigrate) {
        return false;
      }

      try {
        const response = await submitMutation(
          () =>
            updateUserReferenceTypes({
              variables: {
                input: {
                  modalId: field,
                  referenceTypes: sanitizedReferenceTypes,
                },
              },
            }),
          {
            hideSuccessToast: false,
          },
        );

        if (!noTopLevelErrors(response)) {
          return false;
        }

        const nextPreferenceState: SetPrefsArg = {
          referenceVisibilityUpdatedAtByField: {
            [field]:
              response.data?.updateUserReferenceTypes.updatedAt ??
              getCurrentIsoTimestamp(),
          },
          referenceTypeStorageMigrated: true,
        };
        nextPreferenceState[field] = sanitizedReferenceTypes;
        setPrefs(nextPreferenceState);

        return true;
      } catch (error) {
        reportCustomError(error as Error);
        return false;
      }
    },
    [
      migrateReferenceVisibilityPreferences,
      setPrefs,
      updateUserReferenceTypes,
      useReferenceVisibilityApi,
    ],
  );

  useEffect(() => {
    if (!useReferenceVisibilityApi) {
      return;
    }

    if (statePrefs.referenceTypeStorageMigrated) {
      return;
    }

    void migrateReferenceVisibilityPreferences();
  }, [
    migrateReferenceVisibilityPreferences,
    statePrefs.referenceTypeStorageMigrated,
    useReferenceVisibilityApi,
  ]);

  return [
    statePrefs,
    {
      setPrefs,
      removePrefs,
      migrateReferenceVisibilityPreferences,
      getReferenceVisibilityPreferences,
      saveReferenceVisibilityPreferences,
    },
  ];
}

interface PrefsContextType
  extends UserPreferencesState,
    UserPreferencesActions {}

const defaultPrefsCtx: PrefsContextType = {
  ...getInitialPrefsFromStorage(),
  setPrefs: noop,
  removePrefs: noop,
  migrateReferenceVisibilityPreferences: async () => false,
  getReferenceVisibilityPreferences: async () => [],
  saveReferenceVisibilityPreferences: async () => false,
};

const PrefsContext = createContext<PrefsContextType>(defaultPrefsCtx);

const UserPreferences: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [prefs, actions] = useUserPrefs();

  const value = useMemo(() => {
    return { ...prefs, ...actions };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs]);

  return (
    <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>
  );
};

const useUserPreferences = (): PrefsContextType => {
  return useContext(PrefsContext);
};

export { UserPreferences, useUserPreferences };
